import { useMemo } from "react";

import type { RegionCode } from "@/api/filter";
import { useConfigureOptions } from "@/hooks/useConfigureOptions";
import {
  previewConfigureSelection,
  type ConfigureSelectionRows,
} from "@/utils/configure-selection-preview";

export interface UseConfigureSelectionPreviewArgs {
  productId: string | null;
  region: RegionCode;
  applicationIds: string[];
  mspAutoFeedEnabled: boolean;
  /** Comparison cost preview `summary.requiredMachines` (line quantities only). */
  requiredMachines: number;
  enabled: boolean;
}

export interface ConfigureSelectionPreviewState {
  data: ConfigureSelectionRows | null;
  /** True while the current application/configuration key is not settled. */
  isLoading: boolean;
  isError: boolean;
}

/**
 * The configuration Configure Solution would show for `productId` — used by the
 * RFQ popup when it is opened straight from the Selector, so its option count
 * and frozen snapshot match the modal's. Shares `useConfigureOptions`, so it
 * reuses the modal's cached response instead of refetching.
 */
export function useConfigureSelectionPreview(
  args: UseConfigureSelectionPreviewArgs,
): ConfigureSelectionPreviewState {
  const query = useConfigureOptions({
    productId: args.productId,
    region: args.region,
    applicationIds: args.applicationIds,
    mspAutoFeedEnabled: args.mspAutoFeedEnabled,
    enabled: args.enabled,
  });
  const response = query.data;

  const data = useMemo(() => {
    if (!args.enabled || !args.productId || !response?.available) return null;
    return previewConfigureSelection({
      response,
      productId: args.productId,
      region: args.region,
      mspAutoFeedEnabled: args.mspAutoFeedEnabled,
      requiredMachines: args.requiredMachines,
    });
  }, [
    response,
    args.enabled,
    args.productId,
    args.region,
    args.mspAutoFeedEnabled,
    args.requiredMachines,
  ]);

  return {
    // `keepPreviousData` may expose the old application's option response while
    // a new key loads. Never surface that placeholder as the current RFQ state.
    data: query.isPlaceholderData ? null : data,
    isLoading:
      args.enabled &&
      (query.isPending || query.isFetching || query.isPlaceholderData),
    isError: args.enabled && query.isError,
  };
}
